import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Container, Row, Col, Pagination, Spinner } from 'react-bootstrap';
import { useStockLedger } from '../../contextjs/StockLedgerContext';
import { useNavigate } from 'react-router-dom';
import { fetchCurrentStore, fetchStores } from '../../api/api';

const StockLedger = () => {
    const {ledger, loadLedger, totalCount, loading} = useStockLedger();
    const navigate = useNavigate();
    const today = new Date().toISOString().split('T')[0];
    const [filters, setFilters] = useState({product_name: '', movement_type: '', reference_id: '', store_id: '',
        start_date: today, end_date: today});
    const [currentStore, setCurrentStore] = useState(null);
    const [stores, setStores] = useState([]);
    const [page, setPage] = useState(1);
    const pageSize = 20;

    const handleBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        loadLedger(filters, page, pageSize);
    }, [page, filters]); // trigger on mount and filter changes

    useEffect(() => {
        const loadStoreContext = async () => {
            try {
                const [activeStore, availableStores] = await Promise.all([
                    fetchCurrentStore(),
                    fetchStores(),
                ]);
                setCurrentStore(activeStore || null);
                setStores(availableStores?.results || availableStores || []);
            } catch (error) {
                console.error('Error loading store context for stock ledger:', error);
            }
        };

        loadStoreContext();
    }, []);

    const handleFilterChange = (e) => setFilters(prev => ({...prev, [e.target.name]: e.target.value}));

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        loadLedger(filters, 1, pageSize);
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <Container className="mt-4">
            <div className="back-button-container">
                <Button variant="danger" onClick={handleBack}>Back</Button>
            </div>
            <h2 className="text-center mb-3">Stock Movement Ledger</h2>

            <Form onSubmit={handleFilterSubmit} className="mb-3">
                <Row className="align-items-end">
                    <Col md={3}>
                        <Form.Label>Product Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="product_name"
                            value={filters.product_name}
                            onChange={handleFilterChange}
                            placeholder="Enter Product Name"
                        />
                    </Col>
                    {currentStore?.code === 'WH' && (
                        <Col md={3}>
                            <Form.Label>Store</Form.Label>
                            <Form.Select
                                name="store_id"
                                value={filters.store_id}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Stores</option>
                                {stores.map((store) => (
                                    <option key={store.id} value={store.id}>
                                        {store.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                    )}
                    <Col md={3}>
                        <Form.Label>Movement Type</Form.Label>
                        <Form.Select
                            name="movement_type"
                            value={filters.movement_type}
                            onChange={handleFilterChange}
                        >
                            <option value="">All Types</option>
                            <option value="PURCHASE">Purchase</option>
                            <option value="PURCHASE_RETURN">Purchase Return</option>
                            <option value="SALE">Sale</option>
                            <option value="SALE_RETURN">Sale Return</option>
                            <option value="ADJUSTMENT">Adjustment</option>
                            <option value="TRANSFER_IN">Transfer In</option>
                            <option value="TRANSFER_OUT">Transfer Out</option>
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label>Reference ID</Form.Label>
                        <Form.Control
                            type="text"
                            name="reference_id"
                            value={filters.reference_id}
                            onChange={handleFilterChange}
                            placeholder="Invoice/Order No."
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="start_date"
                            value={filters.start_date}
                            onChange={handleFilterChange}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                            type="date"
                            name="end_date"
                            value={filters.end_date}
                            onChange={handleFilterChange}
                        />
                    </Col>
                    <Col md={3}>
                        <Button type="submit" className="btn btn-primary w-100">Filter</Button>
                    </Col>
                </Row>
            </Form>

            {loading ? (
                <div className="text-center my-4">
                    <Spinner animation="border" role="status" />
                </div>
            ) : (
                <>
                    {/* Ledger Table */}
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Store</th>
                                <th>Product</th>
                                <th>Movement Type</th>
                                <th>Flow Detail</th>
                                <th>Qty In</th>
                                <th>Qty Out</th>
                                <th>Balance</th>
                                <th>Reference</th>
                                <th>Remarks</th>
                                <th>Recorded By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ledger.length > 0 ? ledger.map((entry) => (
                                <tr key={entry.id}>
                                    <td>{new Date(entry.date).toLocaleDateString()}</td>
                                    <td>{entry.store_name || '_'}</td>
                                    <td>{entry.product_name}</td>
                                    <td>{entry.movement_type_label || entry.movement_type}</td>
                                    <td>{entry.flow_detail || '_'}</td>
                                    <td>{entry.quantity_in}</td>
                                    <td>{entry.quantity_out}</td>
                                    <td>{entry.balance_after}</td>
                                    <td>{entry.reference_id || '_'}</td>
                                    <td>{entry.remarks || '_'}</td>
                                    <td>{entry.recorded_by_name || '_'}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="11" className="text-center">No entries found</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Pagination className="justify-content-center mt=3">

                            {/* Previous Page */}
                            <Pagination.Prev
                                disabled={page === 1}
                                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                            />

                            {/* page numbers (window of 5 pages) */}
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(num => num === 1 || num === totalPages || (num >= page - 2 && num <= page + 2))
                                .map((num, index, arr) => {
                                    // insert ellipsis for gaps
                                    const prevNum = arr[index - 1];
                                    if (prevNum && num - prevNum > 1) {
                                        return (
                                            <React.Fragment key={num}>
                                                <Pagination.Ellipsis disabled />
                                                <Pagination.Item
                                                    key={`page-${num}`}
                                                    active={num === page}
                                                    onClick={() => setPage(num)}
                                                >
                                                    {num}
                                                </Pagination.Item>
                                            </React.Fragment>
                                        );
                                    }
                                    return (
                                        <Pagination.Item
                                            key={`page-${num}`}
                                            active={num === page}
                                            onClick={() => setPage(num)}
                                        >
                                            {num}
                                        </Pagination.Item>
                                    );
                                })}

                            {/* Next button */}
                            <Pagination.Next
                                disabled={page === totalPages}
                                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                            />
                        </Pagination>
                    )}
                </>
            )}
        </Container>
    );
};

export default StockLedger;
